import { HttpErrorResponse } from '@angular/common/http';
import {
  Signal,
  ViewContainerRef,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { FormControlStatus } from '@angular/forms';

import { Observable, of, race, take } from 'rxjs';

import { InfoboxComponent } from '@core/components-infobox-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import { TellentCurrentUserDataFacade, TellentMembersDataFacade } from '@core/tellent-logic';
import {
  TeamMemberStatus,
  TellentClientDialogResult,
  TellentMember,
  TellentOrgAbilities,
} from '@core/tellent-types';
import { resolveMemberFullName } from '@core/tellent-util';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  DialogBodyComponent,
  type DialogComponentI,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  DialogService,
  ProtectedControlsDialogComponent,
  ProtectedControlsService,
} from '@core/user-interface-legacy';

import { EditMemberPermissionsComponent } from './edit-member-permissions/edit-member-permissions.component';
import { AbilityEditLevel, AbilityGroupViewModel } from './edit-member-permissions/types';
import { EditMemberDialogData } from '../../edit-member-admin-dialog.component';
import { EditMemberHeaderComponent } from '../../edit-member-header/edit-member-header.component';

export type EditPermissionDialogData = {
  memberId: string;
};

type EditPermissionDialogResult = TellentClientDialogResult<any>;

@Component({
  selector: 'edit-member-permissions-tab',
  imports: [
    DialogBodyComponent,
    InfoboxComponent,
    EditMemberPermissionsComponent,
    ButtonComponent,
    DialogFooterComponent,
    TranslatePipe,
    ButtonLoadingDirective,
    EditMemberHeaderComponent,
    DialogHeaderComponent,
  ],
  providers: [ProtectedControlsService],
  templateUrl: './edit-member-permissions-dialog.component.html',
  styleUrl: './edit-member-permissions-dialog.component.less',
})
export class EditMemberPermissionsDialogComponent
  implements DialogComponentI<EditPermissionDialogData, EditPermissionDialogResult>
{
  private readonly currentUserFacade = inject(TellentCurrentUserDataFacade);
  private readonly membersDataFacade = inject(TellentMembersDataFacade);
  private readonly dialog = inject(DialogService);
  private readonly vcRef = inject(ViewContainerRef);

  protected readonly TeamMemberStatus = TeamMemberStatus;

  public memberGuid: string;

  public protectedControlsSvc = inject(ProtectedControlsService);
  public dialogRef = inject(DialogRef);
  public destroyRef = inject(DestroyRef);
  public pending = signal<boolean>(false);
  public state = signal<{
    value: TellentOrgAbilities[];
    disabled: boolean;
    selectedGroup: AbilityGroupViewModel | null | undefined;
  }>({
    selectedGroup: null,
    value: [],
    disabled: true,
  });

  public memberData: Signal<TellentMember | null>;
  public adminData: Signal<TellentMember | null>;

  public isOwner: Signal<boolean | null>;
  public isSelf: Signal<boolean | null>;
  public isEmptyAdministrativeAccess: Signal<boolean>;
  public fullName: Signal<string | null>;

  public setDialogData(data: EditMemberDialogData): void {
    this.memberGuid = data.memberId;

    this.memberData = this.membersDataFacade.teamMemberById(this.memberGuid);
    this.adminData = this.currentUserFacade.organizationMembership;

    this.isOwner = computed(() => !!this.memberData()?.owner);

    this.isSelf = computed(() => {
      const member = this.memberData();
      const admin = this.adminData();
      return member && admin ? member.userGuid === admin.userGuid : null;
    });

    this.isEmptyAdministrativeAccess = computed(() => {
      const { selectedGroup, value } = this.state();
      return selectedGroup?.id === AbilityEditLevel.User && value.length === 0;
    });

    this.fullName = computed(() => {
      const memberData = this.memberData();
      return memberData ? resolveMemberFullName(memberData) : null;
    });

    this.membersDataFacade.fetchOne(data.memberId);
    this.protectedControlsSvc.discard();
  }

  public submit(abilities: TellentOrgAbilities[]): void {
    this.pending.set(true);
    this.membersDataFacade.updateAbilities({
      guid: this.memberGuid,
      abilities,
    });

    race(
      this.membersDataFacade.updateAbilitiesSuccess$,
      this.membersDataFacade.updateAbilitiesFail$,
    )
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(({ payload }) => {
        this.protectedControlsSvc.discard();
        const result = { success: !(payload instanceof HttpErrorResponse), payload };
        this.dialogRef.close(result);
      });
  }

  public cancel(): void {
    this.dialogRef.close();
  }

  public handleValueChange($event: TellentOrgAbilities[]) {
    this.state.set({
      selectedGroup: this.state().selectedGroup,
      value: $event,
      disabled: this.state().disabled,
    });
  }

  public handleStatusChange({
    status,
    pristine,
  }: {
    status: FormControlStatus;
    pristine: boolean;
  }) {
    this.state.set({
      selectedGroup: this.state().selectedGroup,
      value: this.state().value,
      disabled: status === 'DISABLED',
    });
    this.protectedControlsSvc.discard();
  }

  public handleSelectedGroupChange($event: AbilityGroupViewModel | null | undefined): void {
    this.state.set({
      selectedGroup: $event,
      value: this.state().value,
      disabled: this.state().disabled,
    });
  }

  public closeGuard(): Observable<boolean> {
    const { locked } = this.protectedControlsSvc;
    if (locked) {
      return this.dialog.openCustom(ProtectedControlsDialogComponent, {}, {}, this.vcRef)
        .afterClosed;
    } else {
      return of(true);
    }
  }
}
